<template>
  <div>
    <b-table responsive v-handy-scroll v-bind:items="rows" v-bind:fields="fields" no-local-sorting
             v-bind:sort-by.sync="sort.field" v-bind:sort-desc.sync="sortIsDesc"
             striped hover @row-clicked="row => $emit('open', row)" v-bind:busy="loading"
             @sort-changed="onSortChanged"
             style="white-space: nowrap; padding-bottom: 30px; cursor: pointer"
    >
      <template #cell(type)="data">
        <span class="d-inline-flex align-items-center">
          <span class="task-type-ic" :style="{ background: meta(data.value).color }">
            <i :class="meta(data.value).icon"></i>
          </span>
          {{ $t('taskTypes.' + data.value) }}
        </span>
      </template>
      <template #cell(status)="data">
        <b-badge :variant="statusVariant(data.value)">{{ $t('taskStatuses.' + data.value) }}</b-badge>
      </template>
      <template #cell(priority)="data">
        <b-badge :variant="priorityVariant(data.value)">{{ $t('taskPriorities.' + data.value) }}</b-badge>
      </template>
      <template #cell(assigneeName)="data">
        {{ data.value || $t('tasks.unassigned') }}
      </template>
      <template #cell(dueDate)="data">
        <span v-if="data.value" :class="{ 'text-danger font-weight-bold': isOverdue(data.item) }">
          {{ data.value | momentDate }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(creationTime)="data">{{ data.value | momentDateTime }}</template>
    </b-table>

    <div class="mt-2 text-center">
      <div class="d-inline-block">
        <b-pagination v-model="pagination.page" v-bind:total-rows="pagination.total"
                      v-bind:per-page="pagination.size" @page-click="onPageClick" align="center"/>
      </div>
      <div class="d-inline-block">
        <b-select class="d-inline-block" style="width: 70px" size="sm" v-model="pagination.size" @change="refresh">
          <b-select-option v-bind:value="25">25</b-select-option>
          <b-select-option v-bind:value="50">50</b-select-option>
          <b-select-option v-bind:value="100">100</b-select-option>
        </b-select>
        /{{ pagination.total }} {{ $t('menu.tasks') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixinTable from '@/components/mixin/mixinTable'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinBase from '@/components/mixin/mixinBase'
import { taskMeta, STATUS_VARIANT, PRIORITY_VARIANT } from '@/model/task/taskTypeMeta'

export default {
  name: 'TasksTable',
  mixins: [mixinBase, mixinTable, mixinNotifications],
  data: () => ({ rows: [] }),
  props: {
    filters: { default: () => ({}) },
    lazy: { default: () => false },
  },
  watch: {
    filters: { deep: true, handler() { this.pagination.page = 1; this.refresh() } },
  },
  created() {
    this.setPagination(1, 0, 25)
    if (!this.lazy) this.refresh()
  },
  computed: {
    fields() {
      return [
        { key: 'type', label: this.$t('columns.type'), sortable: true },
        { key: 'title', label: this.$t('columns.title'), sortable: true },
        { key: 'status', label: this.$t('columns.status'), sortable: true },
        { key: 'priority', label: this.$t('columns.priority'), sortable: true },
        { key: 'assigneeName', label: this.$t('columns.assignee'), sortable: true },
        { key: 'dueDate', label: this.$t('columns.dueDate'), sortable: true },
        { key: 'createdByName', label: this.$t('columns.createdBy'), sortable: true },
        { key: 'creationTime', label: this.$t('columns.creationTime'), sortable: true },
      ]
    },
  },
  methods: {
    ...mapActions('tasks', ['getAllTasks']),
    meta(type) { return taskMeta(type) },
    statusVariant(s) { return STATUS_VARIANT[s] || 'light' },
    priorityVariant(p) { return PRIORITY_VARIANT[p] || 'light-secondary' },
    isOverdue(task) {
      return !!task.dueDate && task.status !== 'DONE' && this.$moment(task.dueDate).isBefore(this.$moment())
    },
    async refresh() {
      let $this = this
      let loadingToken = this.setLoading()
      let sort = { ...this.sort }
      if (sort.field == null) sort.field = 'creationTime'
      const result = await this.getAllTasks({
        pagination: this.pagination,
        sort,
        filter: this.filters,
      }).catch(err => {
        $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.tasks') }), $this.getErrorDescription(err))
        $this.setLoaded(loadingToken)
      })
      if (result) {
        this.rows = result.data
        this.setMetaDataFromResult(result)
      }
      this.setLoaded(loadingToken)
    },
  },
}
</script>

<style scoped>
.task-type-ic {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 7px; color: #fff; font-size: 0.72rem; margin-right: 8px;
}
</style>
